import StructuredData from '@components/StructuredData';
import AuthContext from '@context/auth';
import {
    homePageStructuredData,
    showAppDownloadBanner,
} from '@helpers/constants';
import { HeadTagData } from '@helpers/headTagData';
import BottomNavigation from '@molecules/BottomNavigation';
import {
    getHomePageConfigurations,
    getOpenConfigurations,
} from '@services/configuration.service';
import { getAuth } from '@services/identity.service';
import useAuthStore from '@store/useAuthStore';
import NextHeadTag from '@ui/molecules/NextHeadTag';
import { useWindowSize } from '@uidotdev/usehooks';
import 'animate.css/animate.min.css';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { useContext, useEffect, useRef, useState } from 'react';
import { logEvent, moengageEventConstants } from '@services/moengage';
import useHomePageDeal from '@components/Homepage/OpportunitiesCarousel/hooks/useHomePageDeal';
import { getHomePageDisplayDeals } from '@services/homepage-deals-service';
import { isValidResponseEntity } from '@helpers/utils';
import { formatHomePageDeals } from '@components/Homepage/OpportunitiesCarousel/utils/helpers';
import { configurationEntityTypes } from '@helpers/enums/configuration-enums';
import moment from 'moment';
import BondDirectoryBanner from '@components/Homepage/BondDirectoryBanner';
import { handleOpportunitiesNavigation } from '@helpers/utils/navigation-utils';
import routes from '@helpers/routes';
import texts from '@components/Homepage/en.json';
import { getHomepageBanners } from '@services/homepage.service';
import { FETCH_BLOGS } from '@services/blogs.service';
import client from '@lib/apollo-client';
import { getwebinars } from '@services/webinars.service';
const FestiveBanner = dynamic(() =>
    import('@components/Homepage/Banner/FestiveBanner')
);
const Banner = dynamic(() => import('@components/Homepage/Banner'));
const ImageBanner = dynamic(() => import('@components/Homepage/ImageBanner'));
const BondPyramidSection = dynamic(() =>
    import('@components/Homepage/BondPyramidSection')
);
const WhyBondsWithJiraaf = dynamic(() =>
    import('@components/Homepage/WhyBondsWithJiraaf')
);
const YoutubeTestimonials = dynamic(() =>
    import('@components/Homepage/YoutubeTestimonials')
);
const LearnMore = dynamic(() => import('@components/Homepage/LearnMore'));
const FaqsHome = dynamic(() => import('@components/Homepage/FaqsHome'));
const OpportunitiesCarouselV2 = dynamic(() =>
    import('@components/Homepage/OpportunitiesCarouselV2')
);
const StartInvesting = dynamic(() =>
    import('@components/Homepage/StartInvesting')
);
const Investors = dynamic(() => import('@components/Homepage/Investors'));
const RegulatedFramework = dynamic(() =>
    import('@components/Homepage/RegulatedFramework')
);
const InvestmentJourney = dynamic(() =>
    import('@components/Homepage/InvestmentJourney')
);
const MediaCoverage = dynamic(() =>
    import('@components/Homepage/MediaCoverage')
);
const NavHeader = dynamic(() => import('@molecules/NavHeader'), {
    ssr: true,
});
const Footer = dynamic(() => import('@molecules/Footer'), {
    ssr: false,
});
const CarouselBanner = dynamic(
    () => import('@components/Homepage/CarouselBanner'),
    { ssr: true }
);
const DownloadAppTicker = dynamic(
    () => import('@components/Homepage/CarouselBanner/DownloadAppTicker'),
    { ssr: true }
);
const bannerConfiguration = Object.freeze({
    AMO_BANNER: 'BannerVersion 2.0',
    CHRISTMAS: 'ChristmasBanner',
    CAROUSEL: 'BannerVersion 3.0',
});

function Homepage({
    configuration,
    homepageDealsResponse,
    muhuratExpiry,
    homepageBanners,
    blogList,
    webinarsList,
}) {
    const {
        signUpModal,
        loginModal,
        investor,
        rmDetails,
        showRMDetails,
        bondFeatureFlag,
    } = useContext(AuthContext);
    const { isLogged } = useAuthStore();
    const auth = getAuth();
    const router = useRouter();
    const [isBottomNavVisible, setIsBottomNavVisible] = useState(false);
    const { width } = useWindowSize();
    const { homePageDeals, error: homePageDealsError } = useHomePageDeal({
        homepageDealsResponse,
    });
    const showMuhuratDays =
        muhuratExpiry && moment(muhuratExpiry).isValid()
            ? moment().isSameOrBefore(moment(muhuratExpiry))
            : false;

    const handleButtonClick = (showSignUpModal = false) => {
        if (showSignUpModal) {
            if (isLogged) {
                router.push(routes.opportunities);
                return;
            }
            signUpModal();
        } else handleOpportunitiesNavigation(isLogged, router);
    };

    useEffect(() => {
        logEvent({ eventName: moengageEventConstants.VIEWED_HOME_PAGE });
    }, []);

    useEffect(() => {
        const handleIntersection = (entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    const animationClass = entry.target.dataset.animation;
                    if (animationClass) {
                        entry.target.classList.add(
                            'animate__animated',
                            animationClass
                        );
                    }
                }
            });
        };

        const observerOptions = {
            root: null,
            rootMargin: '0px',
            threshold: 0.1,
        };

        const observer = new IntersectionObserver(
            handleIntersection,
            observerOptions
        );

        const targets = document.querySelectorAll('[data-animation]');
        targets.forEach((target) => {
            target.classList.add('animatable');
            observer.observe(target);
        });

        return () => {
            targets.forEach((target) => {
                observer.unobserve(target);
            });
        };
    }, []);

    useEffect(() => {
        // if redirect and auth both are present then navigate to the given url else open loginPopup in case only redirect is present
        let redirect = router?.query?.redirect || '';
        if (auth && redirect) {
            router.replace(redirect);
        } else if (redirect) {
            loginModal();
        }
    }, [router?.query]);

    return (
        <>
            <NextHeadTag {...HeadTagData['/']} />
            <StructuredData data={homePageStructuredData} />
            <div
                className={`bg-bgColor-50 ${
                    isBottomNavVisible ? `pb-[68px] xl:pb-0` : 'pb-0'
                }`}
            >
                <div
                    className="overflow-hidden homepage-wrapper"
                    id="pagesindex"
                >
                    <NavHeader
                        hideHeaderOnScroll={true}
                        translucent={false}
                        homePage={true}
                    />
                    <div id="homepageWrapper" className="overflow-hidden">
                        {configuration?.value ===
                        bannerConfiguration.CAROUSEL ? (
                            <CarouselBanner
                                banners={homepageBanners}
                                isLogged={isLogged}
                            />
                        ) : configuration?.value ===
                          bannerConfiguration.AMO_BANNER ? (
                            <ImageBanner
                                config={bannerConfiguration.AMO_BANNER}
                                onClick={() => {
                                    if (isLogged) {
                                        router.push('/opportunities');
                                    } else {
                                        loginModal();
                                    }
                                }}
                            />
                        ) : configuration?.value ===
                          bannerConfiguration.CHRISTMAS ? (
                            <FestiveBanner onButtonClick={handleButtonClick} />
                        ) : (
                            <Banner
                                onButtonClick={() => handleButtonClick(true)}
                                showMuhuratDays={showMuhuratDays}
                                ctaButtonText={texts?.StartInvesting}
                            />
                        )}
                        {showAppDownloadBanner ? (
                            <DownloadAppTicker
                                positionClassName={`relative z-2 ${
                                    isLogged ? 'xl:flex hidden' : ''
                                }`}
                                isLogged={isLogged}
                            />
                        ) : null}
                        <div className="bg-basicWhite relative z-20">
                            {homePageDeals.length > 0 && !homePageDealsError ? (
                                <OpportunitiesCarouselV2
                                    homePageDeals={homePageDeals}
                                    homePageDealsError={homePageDealsError}
                                    signUpModal={signUpModal}
                                    isLogged={isLogged}
                                    opportunities={homePageDeals}
                                    className={'md:mt-20'}
                                    scaleFactor={0.65}
                                />
                            ) : null}{' '}
                            {/* Uncomment to display recent deals */}
                            {/* <RecentDeals onButtonClick={signUpModal} /> */}
                            <BondPyramidSection />
                            <WhyBondsWithJiraaf />
                            <YoutubeTestimonials />
                            {bondFeatureFlag && (
                                <BondDirectoryBanner
                                    className={
                                        showMuhuratDays ? 'mt-16' : 'mt-0'
                                    }
                                />
                            )}
                            <StartInvesting
                                onButtonClick={() => handleButtonClick(true)}
                            />
                            <LearnMore
                                blogList={blogList}
                                webinarsList={webinarsList}
                            />
                            <MediaCoverage />
                            <Investors />
                            <RegulatedFramework />
                            <FaqsHome />
                            <InvestmentJourney
                                onButtonClick={() => handleButtonClick(true)}
                                ctaButtonText={texts?.StartInvesting}
                            />
                        </div>
                        {/* Uncomment to display following sections as and when needed */}
                        {/* <InvestmentOptions /> */}
                        {/* <div className='bg-basicWhite bg-[url(/images/homepage/background-gradient-3.svg)] bg-top bg-cover bg-no-repeat'>
                            <FinancialProsperity />
                        </div> */}
                    </div>
                    <div>
                        <Footer
                            isLightFooter={false}
                            entityType={auth?.entityType}
                        />
                    </div>
                </div>
            </div>
            {width && width < 1280 ? (
                <BottomNavigation
                    investor={investor}
                    visible={true}
                    rmDetails={rmDetails}
                    setIsBottomNavVisible={setIsBottomNavVisible}
                    showRMDetails={showRMDetails}
                    hideAppDownloadBasedOnFooter
                />
            ) : null}
        </>
    );
}

function supportsIntersectionObserver() {
    return !!(
        'IntersectionObserver' in window ||
        'IntersectionObserverEntry' in window ||
        'intersectionRatio' in window.IntersectionObserverEntry.prototype
    );
}

export default Homepage;

export const getStaticProps = async (ctx) => {
    const defaultConfig = {
        type: 'General',
        entityType: 'HomePageBanner',
        value: 'Default',
    };
    const HOME_PAGE_REVALIDATION_CACHE_TIME = parseInt(
        process.env.NEXT_PUBLIC_HOME_PAGE_REVALIDATION_TIME
    );
    try {
        // BANNER CONFIG
        const response = await getHomePageConfigurations(ctx);
        // HOME PAGE DEALS
        const homepageDealsResponse = await getHomePageDisplayDeals(ctx);
        const formattedDeals = {
            ...homepageDealsResponse,
            entity: isValidResponseEntity(homepageDealsResponse)
                ? formatHomePageDeals(homepageDealsResponse?.entity)
                : [],
        };

        let formattedHomepageBanners = [];
        if (
            isValidResponseEntity(response) &&
            response?.entity?.[0]?.value === bannerConfiguration.CAROUSEL
        ) {
            const homepageBanners = await getHomepageBanners(ctx);
            if (isValidResponseEntity(homepageBanners)) {
                formattedHomepageBanners = homepageBanners?.entity;
            }
        }

        // MuhuratDays
        const muhuratDaysResponse = await getOpenConfigurations(
            configurationEntityTypes.MuhuratDays,
            ctx
        );
        const muhuratExpiry =
            isValidResponseEntity(muhuratDaysResponse) &&
            muhuratDaysResponse?.entity?.length
                ? muhuratDaysResponse?.entity?.[0]?.value
                : null;

        //Blogs
        const baseBlogFilters = {
            first: 3,
            after: null,
            category: '',
            searchTerm: '',
        };
        const blogListResult = await client.query({
            query: FETCH_BLOGS,
            variables: baseBlogFilters,
            fetchPolicy: 'no-cache',
        });

        //Webinars
        const webinarsResult = await getwebinars(ctx);
        const webinarsList = isValidResponseEntity(webinarsResult)
            ? webinarsResult.entity
            : [];

        return {
            props: {
                configuration:
                    !isValidResponseEntity(response) ||
                    response?.entity?.length === 0
                        ? defaultConfig
                        : response?.entity?.[0],
                homepageDealsResponse: formattedDeals,
                muhuratExpiry: muhuratExpiry,
                homepageBanners: formattedHomepageBanners,
                blogList: blogListResult?.data?.posts?.nodes || [],
                webinarsList,
            },
            revalidate: HOME_PAGE_REVALIDATION_CACHE_TIME,
        };
    } catch (error) {
        return {
            props: {
                configuration: defaultConfig,
                homepageDealsResponse: {
                    entity: [],
                    status: false,
                    message: 'Something went wrong',
                },
                muhuratExpiry: null,
                homepageBanners: [],
                blogList: [],
                webinarsList: [],
            },
            revalidate: HOME_PAGE_REVALIDATION_CACHE_TIME,
        };
    }
};
